import React, { useState, useEffect } from 'react';
import { Button, Card, CardContent, CardActions, Stack, TextField, Typography, Box } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CreateTemplateRequest, Template } from '../_types';
import errorHelper from 'helpers/errorHelper';
import { ServerErrorResponse } from 'data_sources/constants';
import { neutral } from '_lex/_foundations/phxColors';
import { translate as t } from 'hooks/useTranslations';
import ModulePicker from './ModulePicker';
import datetimeHelper from 'helpers/datetimeHelper';
import JurisdictionPicker from 'components/_pickers/JurisdictionPicker';
import useJurisdictions from 'data_sources/useJurisdictions';

// Extend dayjs with UTC support
dayjs.extend(utc);

interface FormData {
  name: string;
  assignedModule: number | null; // Changed to nullable number to store module ID
  effectiveDate: dayjs.Dayjs | null;
  description: string;
  jurisdiction: string[];
}

// Validation schema - dynamic based on mode
const getValidationSchema = (isEditMode: boolean, isChildMode: boolean, isNameEditable: boolean) =>
  Yup.object().shape({
    name:
      isEditMode && isNameEditable
        ? Yup.string().required(t('performanceReporting.templates.edit.form.name.required'))
        : isEditMode
          ? Yup.string()
          : Yup.string().required(t('performanceReporting.templates.create.form.name.required')),
    assignedModule: isEditMode
      ? Yup.number().nullable()
      : Yup.number().nullable().required(t('performanceReporting.templates.create.form.assignedModule.required')),
    effectiveDate: isEditMode
      ? Yup.mixed().nullable()
      : Yup.mixed()
          .nullable()
          .test(
            'future-date',
            t('performanceReporting.templates.create.form.effectiveDate.validation.futureDate'),
            function (value) {
              if (!value) return true; // Allow empty values
              if (!dayjs.isDayjs(value)) return false;
              const today = dayjs().startOf('day');
              return value.isAfter(today) || value.isSame(today, 'day');
            },
          ),
    description: Yup.string().required(
      isEditMode
        ? t('performanceReporting.templates.edit.form.description.required')
        : t('performanceReporting.templates.create.form.description.required'),
    ),
    jurisdiction:
      isChildMode && !isEditMode
        ? Yup.array()
            .of(Yup.string())
            .min(1, t('performanceReporting.templates.createChild.form.jurisdiction.required'))
        : Yup.array().of(Yup.string()),
  }) as Yup.ObjectSchema<FormData>;

const initialState = {
  name: '',
  assignedModule: null as number | null,
  effectiveDate: null,
  description: '',
  jurisdiction: [] as string[],
};

interface TemplateFormProps {
  onCreate?: (data: CreateTemplateRequest) => Promise<void>;
  onSave?: (data: Partial<Template>) => Promise<void>;
  onCancel: () => void;
  serverErrors?: ServerErrorResponse;
  setServerErrors: (errors: ServerErrorResponse | undefined) => void;
  initialValues?: Template;
  isEditMode?: boolean;
  isChildMode?: boolean;
  isNameEditable?: boolean;
  parentTemplate?: Template;
}

const TemplateForm: React.FC<TemplateFormProps> = ({
  onCreate,
  onSave,
  onCancel,
  serverErrors,
  setServerErrors,
  initialValues,
  isEditMode = false,
  isChildMode = false,
  isNameEditable = false,
  parentTemplate,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [schemaErrors, setSchemaErrors] = useState<any>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const formRef = React.useRef<HTMLFormElement>(null);
  const jurisdictionsQuery = useJurisdictions();

  // Determine if we're editing a child template
  const isChildTemplate = isChildMode || (isEditMode && !!initialValues?.parentTemplateId);

  // Check if template has been published (has lastPublishDate)
  const hasBeenPublished = isEditMode && !!initialValues?.lastPublishDate;

  // Create initial state from provided values or defaults
  const getInitialState = (): FormData => {
    if (initialValues) {
      return {
        name: initialValues.name || '',
        assignedModule: initialValues.assignedModuleId || null,
        effectiveDate: initialValues.effectiveDate ? dayjs(initialValues.effectiveDate) : null,
        description: initialValues.description || '',
        jurisdiction: initialValues.jurisdictionId ? [initialValues.jurisdictionId] : [],
      };
    }
    if (isChildTemplate && parentTemplate) {
      return {
        name: '',
        assignedModule: parentTemplate.assignedModuleId || null,
        effectiveDate: null,
        description: parentTemplate.description || '',
        jurisdiction: [],
      };
    }
    return initialState;
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    trigger,
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: getInitialState(),
    resolver: yupResolver(getValidationSchema(isEditMode, isChildMode, isNameEditable)),
    mode: 'onSubmit',
  });

  // Watch jurisdiction field to auto-generate name in child mode (only for new child templates, not editing)
  const selectedJurisdiction = watch('jurisdiction');

  // Auto-generate name when jurisdiction is selected in child mode (only during creation)
  useEffect(() => {
    if (isChildMode && !isEditMode && parentTemplate && selectedJurisdiction && selectedJurisdiction.length > 0) {
      const jurisdiction = (jurisdictionsQuery.data ?? []).find((j) => j.id === selectedJurisdiction[0]);
      if (jurisdiction) {
        const generatedName = `${jurisdiction.abbreviation} - ${parentTemplate.name}`;
        setValue('name', generatedName, { shouldValidate: true });
      }
    }
  }, [isChildMode, isEditMode, parentTemplate, selectedJurisdiction, jurisdictionsQuery.data]);

  const onSubmit = async (data: FormData) => {
    // Clear any previous errors
    if (setServerErrors) {
      setServerErrors(undefined);
    }
    setSchemaErrors(null);

    // Trigger validation
    const isValid = await trigger();
    if (!isValid) {
      return; // Don't proceed if validation fails
    }

    setIsSubmitting(true);

    try {
      if (isEditMode && onSave) {
        const updateData: Partial<Template> = {
          description: data.description,
          ...(!isNameEditable && {
            effectiveDate: data.effectiveDate ? data.effectiveDate.startOf('day').utc().toISOString() : null,
          }),
          ...(isNameEditable && { name: data.name }),
          // Include jurisdictionId if this is a child template and it's being edited
          ...(isChildTemplate && data.jurisdiction.length > 0 && { jurisdictionId: data.jurisdiction[0] }),
        };
        await onSave(updateData);
      } else if (!isEditMode && onCreate) {
        // Create mode - validate and create new template
        if (data.assignedModule === null) {
          throw new Error('Assigned module is required');
        }

        // Look up jurisdiction name if jurisdiction is selected
        let jurisdictionName: string | undefined;
        if (data.jurisdiction.length > 0) {
          const jurisdiction = (jurisdictionsQuery.data ?? []).find((j) => j.id === data.jurisdiction[0]);
          if (jurisdiction?.name) {
            jurisdictionName = jurisdiction.abbreviation
              ? `${jurisdiction.name} (${jurisdiction.abbreviation})`
              : jurisdiction.name;
          }
        }

        const createRequest: CreateTemplateRequest = {
          name: data.name,
          assignedModuleId: data.assignedModule,
          description: data.description,
          effectiveDate: data.effectiveDate ? data.effectiveDate.startOf('day').utc().toISOString() : null,
          sections: [],
          ...(isChildTemplate && parentTemplate && { parentId: parentTemplate.id }),
          ...(data.jurisdiction.length > 0 && {
            jurisdictionId: data.jurisdiction[0],
            ...(jurisdictionName && { jurisdictionName }),
          }),
        };

        await onCreate(createRequest);
      }
    } catch (error: any) {
      // Handle server errors
      if (error?.errorDetails || error?.errorMessage) {
        if (setServerErrors) {
          setServerErrors(error as ServerErrorResponse);
        }
      } else {
        console.error('Unexpected error:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split('T')[0];

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Card
        sx={{
          border: '1px solid',
          borderColor: neutral[200],
          boxShadow: 'none',
        }}
        className='lex-form'
      >
        <form
          ref={formRef}
          onSubmit={handleSubmit(onSubmit)}
          autoComplete='off'
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant='h5'
              sx={{ mb: 3, fontWeight: 600 }}
            >
              {isEditMode
                ? t('performanceReporting.templates.edit.title')
                : isChildTemplate
                  ? t('performanceReporting.templates.createChild.title')
                  : t('performanceReporting.templates.create.title')}
            </Typography>

            {errorHelper.renderAllErrors(serverErrors, schemaErrors)}

            <Stack spacing={3}>
              {/* Jurisdiction - Show in child mode (create or edit), but not in agency admin edit */}
              {isChildTemplate && !isNameEditable && (
                <Controller
                  name='jurisdiction'
                  control={control}
                  render={({ field }) => (
                    <JurisdictionPicker
                      id='jurisdiction'
                      label={`${t('performanceReporting.templates.createChild.form.jurisdiction.label')}`}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isSubmitting || (isEditMode && hasBeenPublished)}
                      required={true}
                      error={!!errors.jurisdiction}
                      helperText={(errors.jurisdiction?.message as string) || ''}
                      allowMultiple={false}
                      sx={
                        isEditMode && hasBeenPublished
                          ? {
                              '& .react-select__control': {
                                backgroundColor: neutral[100],
                              },
                            }
                          : {}
                      }
                    />
                  )}
                />
              )}

              {/* Template Name */}
              <Controller
                name='name'
                control={control}
                render={({ field }) => {
                  const isNameReadOnly = (isChildTemplate && !isEditMode) || (isEditMode && !isNameEditable);
                  return (
                    <TextField
                      {...field}
                      label={
                        isEditMode
                          ? `${t('performanceReporting.templates.edit.form.name.label')}${isNameEditable ? ' *' : ''}`
                          : `${t('performanceReporting.templates.create.form.name.label')} *`
                      }
                      fullWidth
                      error={!!errors.name}
                      helperText={(errors.name?.message as string) || ''}
                      disabled={isSubmitting || isNameReadOnly}
                      sx={
                        isNameReadOnly
                          ? {
                              '& .MuiInputBase-root': {
                                backgroundColor: neutral[100],
                              },
                              '& .MuiInputBase-input': {
                                color: 'text.disabled',
                              },
                            }
                          : {}
                      }
                    />
                  );
                }}
              />

              {/* Assigned Module */}
              <Controller
                name='assignedModule'
                control={control}
                render={({ field }) => (
                  <ModulePicker
                    id='assignedModule'
                    label={
                      isEditMode
                        ? t('performanceReporting.templates.edit.form.assignedModule.label')
                        : `${t('performanceReporting.templates.create.form.assignedModule.label')}`
                    }
                    value={field.value}
                    onChange={field.onChange}
                    disabled={isSubmitting || isEditMode || isChildTemplate}
                    error={!!errors.assignedModule}
                    helperText={errors.assignedModule?.message as string}
                    required={!isEditMode && !isChildTemplate}
                  />
                )}
              />

              {/* Effective Date - Hidden in agency admin edit */}
              {!isNameEditable && (
                <Controller
                  name='effectiveDate'
                  control={control}
                  render={({ field }) => {
                    const value = field.value ? (dayjs.isDayjs(field.value) ? field.value : dayjs(field.value)) : null;
                    const minDate = dayjs().startOf('day'); // Today or later

                    const pickerErrorText =
                      pickerError === 'invalidDate'
                        ? t('performanceReporting.templates.create.form.effectiveDate.validation.invalidDate')
                        : pickerError === 'minDate'
                          ? t('performanceReporting.templates.create.form.effectiveDate.validation.futureDate')
                          : '';

                    const showError = !!errors.effectiveDate || !!pickerError;
                    const helperText = (errors.effectiveDate?.message as string) || pickerErrorText || '';

                    return (
                      <DatePicker
                        label={
                          isEditMode
                            ? t('performanceReporting.templates.edit.form.effectiveDate.label')
                            : t('performanceReporting.templates.create.form.effectiveDate.label')
                        }
                        value={value}
                        onChange={(d) => field.onChange(d ?? null)}
                        onError={(reason) => setPickerError(reason)}
                        format='MM/DD/YYYY'
                        minDate={isEditMode ? undefined : minDate}
                        disabled={isSubmitting || (isEditMode && hasBeenPublished)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: showError,
                            helperText,
                            placeholder: 'MM/DD/YYYY',
                            sx:
                              isEditMode && hasBeenPublished
                                ? {
                                    '& .MuiInputBase-root': {
                                      backgroundColor: neutral[100],
                                    },
                                    '& .MuiInputBase-input': {
                                      color: 'text.disabled',
                                    },
                                  }
                                : {},
                          },
                        }}
                      />
                    );
                  }}
                />
              )}

              {/* Description */}
              <Controller
                name='description'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={`${
                      isEditMode
                        ? t('performanceReporting.templates.edit.form.description.label')
                        : t('performanceReporting.templates.create.form.description.label')
                    } *`}
                    fullWidth
                    multiline
                    rows={4}
                    error={!!errors.description}
                    helperText={(errors.description?.message as string) || ''}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Stack>

            {/* Created and Last Updated Info - Only show in edit mode */}
            {isEditMode && initialValues && (
              <Box sx={{ mt: 4, p: 3, borderTop: '1px solid', borderColor: neutral[200], display: 'flex', gap: 4 }}>
                <Typography
                  variant='body2'
                  color='text.secondary'
                >
                  Created: {initialValues.createdAt ? datetimeHelper.formatDate(initialValues.createdAt) : '--'}
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                >
                  Last updated: {initialValues.updatedAt ? datetimeHelper.formatDate(initialValues.updatedAt) : '--'}
                </Typography>
              </Box>
            )}
          </CardContent>

          <CardActions
            sx={{
              p: 4,
              pt: 0,
            }}
          >
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant='outlined'
                color='neutral'
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {isEditMode
                  ? t('performanceReporting.templates.edit.buttons.cancel')
                  : t('performanceReporting.templates.create.buttons.cancel')}
              </Button>
              <Button
                variant='contained'
                color='primary'
                type='submit'
                disabled={isSubmitting}
                loading={isSubmitting}
                loadingPosition='end'
              >
                {isEditMode
                  ? t('performanceReporting.templates.edit.buttons.save')
                  : isChildTemplate
                    ? t('performanceReporting.templates.createChild.buttons.create')
                    : t('performanceReporting.templates.create.buttons.create')}
              </Button>
            </Box>
          </CardActions>
        </form>
      </Card>
    </Box>
  );
};

export default TemplateForm;
